import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { centsEur, hoursDe } from "@/lib/documents";
import type { DocumentDetail } from "@/lib/types";
import type { DocumentWrites } from "./useDocument";

export type ItemsKind = "entries" | "shares";

const shortDate = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

interface Row { key: string; id: number; date: string; title: string; meta: string; reserved: string | null }

function rows(detail: DocumentDetail, kind: ItemsKind): Row[] {
  const free: Row[] =
    kind === "entries"
      ? detail.open.entries.map((e) => ({
        key: `e${e.id}`, id: e.id, date: e.date, title: e.description || e.projectName, meta: `${e.projectName} · ${hoursDe(e.billableMinutes)} Std`, reserved: null,
      }))
      : detail.open.shares.map((s) => ({
        key: `s${s.id}`, id: s.id, date: s.valueDate, title: s.payee, meta: [s.note, centsEur(s.rebillCents)].filter(Boolean).join(" · "), reserved: null,
      }));
  const held = detail.reserved
    .filter((r) => (kind === "entries" ? r.kind === "entry" : r.kind === "share"))
    .map((r) => ({ key: `r${r.kind}${r.id}`, id: r.id, date: r.date, title: r.title, meta: r.meta, reserved: r.label }));
  return [...free, ...held].sort((a, b) => a.date.localeCompare(b.date) || a.id - b.id);
}

/** „Aus Buchungen“ / „Ausgabe weiterverrechnen“: the customer's open items, and those on another draft to pull over. */
export function ItemsPanel({ detail, kind, onClose, writes }: { detail: DocumentDetail; kind: ItemsKind | null; onClose: () => void; writes: DocumentWrites }) {
  const [busy, setBusy] = useState(false);
  const list = kind ? rows(detail, kind) : [];
  const free = list.filter((r) => r.reserved === null);

  async function take(ids: number[]) {
    setBusy(true);
    try {
      await writes.addItems(kind === "entries" ? { timeEntryIds: ids } : { expenseShareIds: ids });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={kind !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{kind === "shares" ? "Ausgabe weiterverrechnen" : "Aus Buchungen"}</DialogTitle>
          <DialogDescription>
            {kind === "shares" ? "Offene Ausgaben von " : "Offene Buchungen von "}{detail.document.clientName}
          </DialogDescription>
        </DialogHeader>
        {list.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">{kind === "shares" ? "Keine offenen Ausgaben." : "Keine offenen Buchungen."}</p>
        ) : (
          <ul className="-mx-2 max-h-[60vh] overflow-y-auto">
            {list.map((r) => (
              <li key={r.key} className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted">
                <span className="w-12 shrink-0 text-[0.8125rem] text-muted-foreground tabular-nums">{shortDate(r.date)}</span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm" title={r.title}>{r.title}</span>
                  <span className="truncate text-xs text-muted-foreground" title={r.reserved ? `${r.meta} · ${r.reserved}` : r.meta}>
                    {r.meta}{r.reserved && <> · <span className="text-foreground">{r.reserved}</span></>}
                  </span>
                </span>
                <Button variant="outline" size="sm" className="shrink-0 pointer-coarse:h-11" disabled={busy} onClick={() => take([r.id])}>
                  {r.reserved ? "hierher holen" : "mitnehmen"}
                </Button>
              </li>
            ))}
          </ul>
        )}
        {free.length > 1 && (
          <div className="flex justify-end">
            <Button size="sm" className="pointer-coarse:h-11" disabled={busy} onClick={() => take(free.map((r) => r.id))}>
              Alle {free.length} mitnehmen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
